import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLeads } from '../contexts/LeadsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Delete,
  Today,
} from '@mui/icons-material';

interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  event_type: string;
  event_date: string;
  start_time: string;
  end_time?: string;
  all_day: boolean;
  location?: string;
  lead_id?: number;
  customer_id?: number;
  project_id?: number;
  created_by: number;
  assigned_to: number;
  assigned_to_name?: string;
  customer_name?: string;
  lead_title?: string;
  project_title?: string;
  status: string;
  reminder_minutes: number;
  color?: string;
  created_at?: string;
  updated_at?: string;
}

export default function Calendar() {
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const [currentDate, setCurrentDate] = useState(() => {
    if (dateParam) {
      const parsed = new Date(dateParam);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }
    return new Date();
  });
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const { getAllAppointments, leads } = useLeads();
  const { user } = useAuth();
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventForm, setEventForm] = useState<Partial<CalendarEvent>>({
    event_type: 'appointment',
    status: 'scheduled',
    assigned_to: user?.id || 1,
    all_day: false,
    reminder_minutes: 15,
  });

  // Fetch calendar events from API
  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/calendar/events?start_date=${startDate}&end_date=${endDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDate = (date: string) => {
    return events.filter(event => {
      // Handle both date-only strings and full timestamps
      const eventDate = event.event_date.split('T')[0];
      return eventDate === date;
    });
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getEventTypeColor = (type: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default' } = {
      appointment: 'primary',
      meeting: 'secondary',
      call: 'success',
      task: 'warning',
      reminder: 'error',
      holiday: 'default',
      visit: 'primary',
      quote: 'warning',
      'follow-up': 'secondary',
    };
    return colors[type] || 'default';
  };

  const getEventTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      appointment: 'Appointment',
      meeting: 'Meeting',
      call: 'Call',
      task: 'Task',
      reminder: 'Reminder',
      holiday: 'Holiday',
      visit: 'Visit',
      quote: 'Quote',
      'follow-up': 'Follow-up',
    };
    return labels[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      scheduled: 'warning',
      confirmed: 'primary',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setEventForm(event);
    setEventDialogOpen(true);
  };

  const handleNewEvent = (date?: string) => {
    setSelectedEvent(null);
    setEventForm({
      event_type: 'appointment',
      status: 'scheduled',
      assigned_to: user?.id || 1,
      event_date: date || formatDate(new Date()),
      start_time: '10:00',
      end_time: '11:00',
      all_day: false,
      reminder_minutes: 15,
    });
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedEvent 
        ? `/api/calendar/events/${selectedEvent.id}`
        : '/api/calendar/events';
      
      const response = await fetch(url, {
        method: selectedEvent ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(eventForm),
      });
      
      if (response.ok) {
        fetchEvents();
        setEventDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/calendar/events/${selectedEvent.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          fetchEvents();
          setEventDialogOpen(false);
        }
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  const todayString = formatDate(today);

  // Get today's events
  const todaysEvents = getEventsForDate(todayString);
  const upcomingEvents = events
    .filter(event => {
      const eventDate = event.event_date.split('T')[0];
      return new Date(eventDate + 'T' + event.start_time) > new Date();
    })
    .sort((a, b) => {
      const dateA = a.event_date.split('T')[0];
      const dateB = b.event_date.split('T')[0];
      return new Date(dateA + 'T' + a.start_time).getTime() - new Date(dateB + 'T' + b.start_time).getTime();
    })
    .slice(0, 5);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleNewEvent()}
        >
          New Event
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        flexWrap: 'wrap',
        gap: 3, 
        mb: 3 
      }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Events
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {todaysEvents.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                This Week
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {events.filter(e => {
                  const eventDateStr = e.event_date.split('T')[0];
                  const eventDate = new Date(eventDateStr);
                  const weekStart = new Date(today);
                  weekStart.setDate(today.getDate() - today.getDay());
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
                  return eventDate >= weekStart && eventDate <= weekEnd;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Home Visits
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {events.filter(e => e.event_type === 'visit' && e.status !== 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Installations
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {events.filter(e => e.event_type === 'task' && e.status !== 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
        {/* Calendar */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 70%' } }}>
          <Card>
            <CardContent>
              {/* Calendar Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton onClick={handlePreviousMonth}>
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="h5" fontWeight="bold" sx={{ minWidth: 200, textAlign: 'center' }}>
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </Typography>
                  <IconButton onClick={handleNextMonth}>
                    <ChevronRight />
                  </IconButton>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Today />}
                  onClick={handleToday}
                  size="small"
                >
                  Today
                </Button>
              </Box>

              {/* Calendar Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                {/* Day Headers */}
                {dayNames.map((day) => (
                  <Box
                    key={day}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption">
                      {day.substring(0, 3)}
                    </Typography>
                  </Box>
                ))}

                {/* Calendar Days */}
                {days.map((day, index) => {
                  if (day === null) {
                    return <Box key={index} sx={{ minHeight: 100 }} />;
                  }

                  const dateString = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
                  const dayEvents = getEventsForDate(dateString);
                  const isToday = dateString === todayString;

                  return (
                    <Paper
                      key={day}
                      sx={{
                        p: 1,
                        minHeight: 100,
                        border: isToday ? 2 : 1,
                        borderColor: isToday ? 'primary.main' : 'divider',
                        backgroundColor: isToday ? 'primary.50' : 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => {
                        handleNewEvent(dateString);
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={isToday ? 'bold' : 'normal'}
                        color={isToday ? 'primary.main' : 'text.primary'}
                        sx={{ mb: 1 }}
                      >
                        {day}
                      </Typography>

                      {/* Events for this day */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {dayEvents.slice(0, 2).map((event) => (
                          <Tooltip
                            key={event.id}
                            title={
                              <Box>
                                <Typography variant="body2">{event.title}</Typography>
                                {event.description && (
                                  <Typography variant="caption" display="block">
                                    {event.description}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block">
                                  {event.start_time} - {event.end_time || 'Open'}
                                </Typography>
                                {event.customer_name && (
                                  <Typography variant="caption" display="block">
                                    Customer: {event.customer_name}
                                  </Typography>
                                )}
                                {event.assigned_to_name && (
                                  <Typography variant="caption" display="block">
                                    Assigned to: {event.assigned_to_name}
                                  </Typography>
                                )}
                              </Box>
                            }
                            arrow
                          >
                            <Chip
                              label={event.title.length > 15 ? event.title.substring(0, 15) + '...' : event.title}
                              size="small"
                              color={getEventTypeColor(event.event_type)}
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                backgroundColor: event.color,
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}
                            />
                          </Tooltip>
                        ))}
                        {dayEvents.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{dayEvents.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 30%' } }}>
          {/* Today's Events */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Schedule
              </Typography>
              {todaysEvents.length === 0 ? (
                <Typography color="text.secondary">
                  No events scheduled for today
                </Typography>
              ) : (
                <List>
                  {todaysEvents.map((event) => (
                    <ListItem
                      key={event.id}
                      sx={{ px: 0, cursor: 'pointer' }}
                      onClick={() => handleEventClick(event)}
                    >
                      <ListItemText
                        primary={event.title}
                        secondary={
                          <Box>
                            <Typography variant="caption" component="div">
                              {event.start_time} - {event.end_time}
                            </Typography>
                            {event.customer_name && (
                              <Typography variant="caption" component="div">
                                {event.customer_name}
                              </Typography>
                            )}
                            {event.location && (
                              <Typography variant="caption" component="div">
                                Location: {event.location}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={event.status}
                          size="small"
                          color={getStatusColor(event.status)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Appointments from Leads */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Lead Appointments
              </Typography>
              {(() => {
                const appointments = getAllAppointments();
                const futureAppointments = appointments
                  .filter(apt => new Date(`${apt.appointment_date}T${apt.appointment_time}`) > new Date())
                  .sort((a, b) => new Date(`${a.appointment_date}T${a.appointment_time}`).getTime() - 
                                  new Date(`${b.appointment_date}T${b.appointment_time}`).getTime())
                  .slice(0, 5);
                
                return futureAppointments.length === 0 ? (
                  <Typography color="text.secondary">
                    No appointments scheduled from leads
                  </Typography>
                ) : (
                  <List>
                    {futureAppointments.map((appointment) => {
                      const lead = leads.find(l => l.id === appointment.lead_id);
                      return (
                        <ListItem key={appointment.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={`${appointment.appointment_type} - ${lead?.customer_name || 'Unknown'}`}
                            secondary={
                              <Box>
                                <Typography variant="caption" component="div">
                                  {new Date(appointment.appointment_date).toLocaleDateString()} at {appointment.appointment_time}
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Assigned to: {appointment.assigned_user}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={appointment.status}
                              size="small"
                              color={appointment.status === 'scheduled' ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                );
              })()}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Other Calendar Events
              </Typography>
              <List>
                {upcomingEvents.map((event) => (
                  <ListItem
                    key={event.id}
                    sx={{ px: 0, cursor: 'pointer' }}
                    onClick={() => handleEventClick(event)}
                  >
                    <ListItemText
                      primary={event.title}
                      secondary={
                        <Box>
                          <Typography variant="caption" component="div">
                            {new Date(event.event_date.split('T')[0]).toLocaleDateString()} at {event.start_time}
                          </Typography>
                          {event.customer_name && (
                            <Typography variant="caption" component="div">
                              {event.customer_name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={getEventTypeLabel(event.event_type)}
                        size="small"
                        color={getEventTypeColor(event.event_type)}
                        variant="outlined"
                        sx={{ backgroundColor: event.color }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedEvent ? 'Edit Event' : 'New Event'}
            {selectedEvent && (
              <IconButton onClick={handleDeleteEvent} color="error">
                <Delete />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Event Title"
              value={eventForm.title || ''}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              required
            />
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <FormControl fullWidth required>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={eventForm.event_type || 'appointment'}
                    label="Event Type"
                    onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                  >
                    <MenuItem value="appointment">Appointment</MenuItem>
                    <MenuItem value="meeting">Meeting</MenuItem>
                    <MenuItem value="call">Call</MenuItem>
                    <MenuItem value="task">Task</MenuItem>
                    <MenuItem value="reminder">Reminder</MenuItem>
                    <MenuItem value="holiday">Holiday</MenuItem>
                    <MenuItem value="visit">Visit</MenuItem>
                    <MenuItem value="quote">Quote</MenuItem>
                    <MenuItem value="follow-up">Follow-up</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={eventForm.status || 'scheduled'}
                    label="Status"
                    onChange={(e) => setEventForm({ ...eventForm, status: e.target.value as any })}
                  >
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={eventForm.description || ''}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={eventForm.event_date || ''}
                  onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={eventForm.start_time || ''}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={eventForm.end_time || ''}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Location"
              value={eventForm.location || ''}
              onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={eventForm.assigned_to || user?.id || 1}
                    label="Assigned To"
                    onChange={(e) => setEventForm({ ...eventForm, assigned_to: Number(e.target.value) })}
                  >
                    <MenuItem value={1}>Tony Gordon</MenuItem>
                    <MenuItem value={2}>Wanda Gordon</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <FormControl fullWidth>
                  <InputLabel>Reminder</InputLabel>
                  <Select
                    value={eventForm.reminder_minutes || 0}
                    label="Reminder"
                    onChange={(e) => setEventForm({ ...eventForm, reminder_minutes: Number(e.target.value) })}
                  >
                    <MenuItem value={0}>No reminder</MenuItem>
                    <MenuItem value={15}>15 minutes before</MenuItem>
                    <MenuItem value={30}>30 minutes before</MenuItem>
                    <MenuItem value={60}>1 hour before</MenuItem>
                    <MenuItem value={1440}>1 day before</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Lead ID (optional)"
                  type="number"
                  value={eventForm.lead_id || ''}
                  onChange={(e) => setEventForm({ ...eventForm, lead_id: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  label="Project ID (optional)"
                  type="number"
                  value={eventForm.project_id || ''}
                  onChange={(e) => setEventForm({ ...eventForm, project_id: e.target.value ? Number(e.target.value) : undefined })}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEvent} variant="contained">
            {selectedEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, display: { xs: 'block', sm: 'none' } }}>
        <Fab color="primary" onClick={() => handleNewEvent()}>
          <Add />
        </Fab>
      </Box>
    </Box>
  );
}